import {
  Badge,
  Banner,
  Button,
  ButtonList,
  Column,
  Columns,
  DropdownButton,
  DropdownButtonContext,
  EmptyState,
  FlexBox,
  Icon,
  Spinner,
  Tabs,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors, typography } from '@dixa/lib-shared-tokens';
import { ampli } from 'lib-shared-tracking';
import type { OpeningHours, Schedule } from 'lib-shared-types-api';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSetState } from 'react-use';

import { flowsApi } from 'apis/flows';
import { ReviewConflictsModal } from 'components/settings/components/sections/openingHours/components/reviewConflictsModal/reviewConflictsModal';
import { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useNavigatePrompt } from 'utils/url/useNavigatePrompt';
import { getUserDisplayName, useCurrentUser } from 'utils/user';

import { RenameModal } from '../../../../components/renameModal/renameModal';
import {
  ActiveView,
  Errors,
  ListItemType,
  ModifiedListItem,
  TabType,
  useScheduleContext,
  ViewType,
} from '../../schedule.context';

import { ImportHolidaysModal } from './components/ImportHolidaysModal/ImportHolidaysModal';
import { BusinessHoursList } from './components/businessHoursList/businessHoursList';
import { ExceptionsList } from './components/exceptionsList/exceptionsList';
import { Preview } from './components/preview/preview';
import { RecoverHolidaysModal } from './components/recoverHolidaysModal/recoverHolidaysModal';
import { TimezoneModal } from './components/timezoneModal/timezoneModal';
import { getDeletedHolidays } from './utils/getDeletedHolidays';

const viewTabs: Record<TabType, string> = {
  businessHours: 'Business hours',
  exceptions: 'Exceptions',
};

export type ScheduleEditPageState = {
  isCreatingFirstSchedule?: boolean;
} | null;

type ModalType = 'import' | 'recover' | 'rename' | 'review' | 'timezone';
type State = Pick<Schedule, 'name' | 'timezone'> & {
  audioMessage: AnnouncementItem;
  countryCode: Schedule['country_code'];
};

function hasErrorIntervalsError(errors: Errors): boolean {
  return errors.flat().some((error) => error?.length);
}

export const ScheduleItem = (): JSX.Element => {
  const [{ audioMessage, countryCode, name, timezone }, setState] =
    useSetState<State>({
      audioMessage: {
        url: '',
        label: '',
        author: '',
      },
      countryCode: null,
      name: '',
      timezone: '',
    });
  const [activeModalType, setActiveModalType] = useState<null | ModalType>(
    null,
  );

  const notify = useNotifications();
  const {
    activeView,
    autoReply,
    conflicts,
    holidays,
    initialSchedule,
    intervalsErrors,
    listItems: { [ListItemType.Exception]: exceptions },
    meta: { isError },
    modifiedListItems,
    refetchSchedules,
    setActiveView,
    setAutoReply,
    setInitialState,
    setModifiedListItems,
  } = useScheduleContext();
  const { id } = useAppPathParams('/settings/businesshours/:id');
  const { user } = useCurrentUser();
  const agentName = getUserDisplayName(user);

  const [updateSchedule, { isLoading }] =
    flowsApi.useUpdateFlowScheduleMutation();
  const [readCountry, { data: country }] =
    flowsApi.useLazyReadFlowsSchedulesCountryQuery();

  useEffect(() => {
    if (initialSchedule) {
      setState({
        audioMessage: initialSchedule.announcement,
        countryCode: initialSchedule.country_code,
        name: initialSchedule.name,
        timezone: initialSchedule.timezone,
      });
    }
  }, [initialSchedule, setState]);

  useEffect(() => {
    if (countryCode) {
      readCountry({ country_code: countryCode });
    }
  }, [countryCode, readCountry]);

  const deletedHolidays = useMemo(
    () => getDeletedHolidays({ agentName, exceptions, holidays }),
    [agentName, exceptions, holidays],
  );

  const conflictedDates = [
    ...new Set(Object.values(conflicts).map(({ next_date }) => next_date)),
  ];

  const handleSetActiveView = useCallback(
    (key: keyof ActiveView, type: TabType | ViewType) => {
      if (key === 'tab') {
        ampli.businessHoursScheduleTabClicked({ view: type });
      }

      if (activeView[key] !== type) {
        setActiveView({
          ...activeView,
          [key]: type,
          ...(key === 'tab' && { menu: 'hours' }),
        });
      }
    },
    [activeView, setActiveView],
  );

  const handleSetActiveModalType = useCallback(
    (type?: ModalType, toggleDropdown?: VoidFunction) => () => {
      if (!type) {
        setActiveModalType(null);
        return;
      }

      switch (type) {
        case 'import':
          ampli.businessHoursScheduleOptionsMenuImportHolidaysClicked();
          break;
        case 'recover':
          ampli.businessHoursScheduleOptionsMenuRecoverHolidaysClicked();
          break;
        case 'rename':
          ampli.businessHoursScheduleOptionsMenuRenameClicked();
          break;
        case 'timezone':
          ampli.businessHoursScheduleOptionsMenuTimezoneClicked();
          break;
      }

      setActiveModalType(type);
      toggleDropdown?.();
    },
    [],
  );

  const handleAddHolidays = useCallback(
    (modifiedListItems: ModifiedListItem, countryCode?: string): void => {
      setModifiedListItems(ListItemType.Exception, modifiedListItems);

      if (countryCode) {
        setState({ countryCode });
      }
    },
    [setModifiedListItems, setState],
  );

  const handleResolveConflicts = useCallback(
    (modifiedItems: Record<string, OpeningHours>) => {
      setModifiedListItems(ListItemType.Exception, modifiedItems);
    },
    [setModifiedListItems],
  );

  const handleSaveSchedule = useCallback(async () => {
    ampli.businessHoursScheduleSaveScheduleButtonClicked();

    const openingHours = Object.values(modifiedListItems).flatMap((listItems) =>
      Object.values(listItems),
    );

    if (initialSchedule) {
      const payload = {
        announcement: audioMessage,
        auto_reply_template_id: autoReply,
        country_code: countryCode,
        name,
        timezone,
        version: initialSchedule.version,
        ...(openingHours?.length && { opening_hours: openingHours }),
      };

      const res = await updateSchedule({ id, schedule: payload });

      if ('data' in res) {
        notify(
          {
            title: 'Schedule updated!',
            type: 'success',
            time: 5000,
          },
          NotificationType.Success,
        );

        setInitialState();
        refetchSchedules();
      } else {
        notify(
          {
            title: `An error occurred when trying to update the schedule.`,
          },
          NotificationType.Error,
        );
      }
    }
  }, [
    audioMessage,
    autoReply,
    countryCode,
    id,
    initialSchedule,
    modifiedListItems,
    name,
    timezone,
    notify,
    refetchSchedules,
    setInitialState,
    updateSchedule,
  ]);

  const hasBusinessHoursIntervalsError = hasErrorIntervalsError(
    intervalsErrors.Standard,
  );
  const hasExceptionsIntervalsError = hasErrorIntervalsError(
    intervalsErrors.Exception,
  );
  const hasIntervalsError =
    hasBusinessHoursIntervalsError || hasExceptionsIntervalsError;

  const hasChanges =
    name !== initialSchedule?.name ||
    timezone !== initialSchedule?.timezone ||
    autoReply !== initialSchedule.auto_reply_template_id ||
    !isEqual(initialSchedule?.announcement, audioMessage) ||
    Object.values(modifiedListItems).some(
      (modifiedItem) => Object.keys(modifiedItem).length,
    );

  useNavigatePrompt(hasChanges);

  const hasConflict = !!Object.keys(conflicts).length;
  const isSaveDisabled = hasIntervalsError || !hasChanges || hasConflict;

  const getTooltipContent = () => {
    if (hasIntervalsError && hasConflict) {
      return 'You can’t save a schedule with conflicting time slots and conflicting dates';
    }
    return hasIntervalsError
      ? 'You can’t save a schedule with conflicting time slots'
      : 'You can’t save a schedule with conflicting dates';
  };

  const hasDeletedHolidays = !!deletedHolidays.length;

  const tabs = useMemo(
    () =>
      Object.entries(viewTabs).map(([key, value]) => {
        const slotStart = (
          <Icon
            name="exclamation-triangle-filled"
            color={colors.orange['800']}
          />
        );

        const shouldRenderSlotStart =
          (key === 'businessHours' && hasBusinessHoursIntervalsError) ||
          (key === 'exceptions' &&
            (hasExceptionsIntervalsError || hasConflict));

        return {
          id: key,
          label: value,
          ...(shouldRenderSlotStart && { slotStart }),
        };
      }),
    [hasBusinessHoursIntervalsError, hasConflict, hasExceptionsIntervalsError],
  );

  if (initialSchedule) {
    return (
      <>
        <Columns>
          <Column>
            <Column.Row slottable>
              <Column.Slot placement="start">
                <FlexBox gap="200" alignItems="center">
                  <Text.Heading>{name}</Text.Heading>

                  <Badge variant="secondary" size="medium">
                    {`Used in ${initialSchedule.number_of_flows} ${
                      initialSchedule.number_of_flows === 1 ? 'flow' : 'flows'
                    }`}
                  </Badge>
                </FlexBox>
              </Column.Slot>

              <Column.Slot placement="end" noVerticalPadding>
                <FlexBox alignItems="center" gap="200">
                  <DropdownButton
                    buttonProps={{ variant: 'secondary' }}
                    fixed
                    icon="dots-v"
                    noArrow
                    onToggle={(isOpen: boolean) => {
                      if (isOpen) {
                        ampli.businessHoursScheduleOptionsMenuOpened();
                      }
                    }}
                    dropdown={
                      <DropdownButtonContext.Consumer>
                        {({ toggleDropdown }) => (
                          <ButtonList>
                            <ButtonList.Item
                              onClick={handleSetActiveModalType(
                                'rename',
                                toggleDropdown,
                              )}
                            >
                              Rename schedule
                            </ButtonList.Item>
                            <ButtonList.Item
                              onClick={handleSetActiveModalType(
                                'timezone',
                                toggleDropdown,
                              )}
                            >
                              Edit timezone
                            </ButtonList.Item>
                            <ButtonList.Item
                              isDisabled={!!countryCode && !hasDeletedHolidays}
                              onClick={handleSetActiveModalType(
                                countryCode ? 'recover' : 'import',
                                toggleDropdown,
                              )}
                            >
                              {countryCode
                                ? 'Recover deleted holidays'
                                : 'Connect holiday calendar'}
                            </ButtonList.Item>
                          </ButtonList>
                        )}
                      </DropdownButtonContext.Consumer>
                    }
                  />

                  <Button
                    active={activeView.view === 'preview'}
                    onClick={() => {
                      ampli.businessHoursSchedulePreviewButtonClicked({
                        view: activeView.view,
                      });
                      handleSetActiveView(
                        'view',
                        activeView.view === 'edit' ? 'preview' : 'edit',
                      );
                    }}
                    variant="secondary"
                  >
                    Preview
                  </Button>

                  <Tooltip
                    content={getTooltipContent()}
                    dark
                    disabled={!hasIntervalsError && !hasConflict}
                    side="left"
                  >
                    <Button
                      disabled={isSaveDisabled}
                      onClick={handleSaveSchedule}
                      isLoading={isLoading}
                    >
                      Save changes
                    </Button>
                  </Tooltip>
                </FlexBox>
              </Column.Slot>
            </Column.Row>

            <Column.Row noVerticalPadding>
              <FlexBox flexDirection="column" gap="200">
                <FlexBox alignItems="center" gap="200">
                  <Icon name="clock-three" />
                  <Text.Paragraph>{timezone}</Text.Paragraph>
                </FlexBox>

                <FlexBox alignItems="center" gap="200">
                  <Icon name="calendar" />

                  {country ? (
                    <Text.Paragraph>{country.name}</Text.Paragraph>
                  ) : (
                    <Text.Paragraph color={typography.color.tertiary}>
                      No holiday calendar imported
                    </Text.Paragraph>
                  )}
                </FlexBox>
              </FlexBox>
            </Column.Row>

            {!!conflictedDates.length && (
              <Column.Row noMinHeight>
                <Banner
                  variant="warning"
                  headline={`Please review ${conflictedDates.length} conflicting dates`}
                  description={
                    <Text.Paragraph variant="4">
                      Select which business hours apply on the conflicting dates
                      for the schedule {name}.
                    </Text.Paragraph>
                  }
                  actionSlot={
                    <Button
                      onClick={() => {
                        ampli.businessHoursScheduleConflictReviewButtonClicked();
                        setActiveModalType('review');
                      }}
                      variant="secondary"
                    >
                      Review conflicts
                    </Button>
                  }
                />
              </Column.Row>
            )}

            {activeView.view === 'edit' && (
              <>
                <Column.Row noVerticalPadding noMinHeight borderBottom>
                  <Tabs
                    onChange={(type) =>
                      handleSetActiveView('tab', type as TabType)
                    }
                    selectedId={activeView.tab}
                    tabs={tabs}
                  />
                </Column.Row>

                {activeView.tab === 'businessHours' && (
                  <BusinessHoursList
                    audioMessage={audioMessage}
                    autoReply={autoReply}
                    onAudioMessageChange={(audioMessage) =>
                      void setState({ audioMessage })
                    }
                    onAutoReplyChange={(autoReply) =>
                      void setAutoReply(autoReply || null)
                    }
                  />
                )}
                {activeView.tab === 'exceptions' && <ExceptionsList />}
              </>
            )}

            {activeView.view === 'preview' && (
              <Column.Row size={1}>
                <Preview />
              </Column.Row>
            )}
          </Column>
        </Columns>

        {/* Modals */}
        {activeModalType === 'import' && (
          <ImportHolidaysModal
            onClose={handleSetActiveModalType()}
            onSubmit={handleAddHolidays}
            timezone={timezone}
          />
        )}
        {activeModalType === 'recover' && country && (
          <RecoverHolidaysModal
            country={country}
            holidays={deletedHolidays}
            onClose={handleSetActiveModalType()}
            onSubmit={handleAddHolidays}
          />
        )}
        {activeModalType === 'rename' && (
          <RenameModal
            mode="edit"
            name={name}
            onClose={handleSetActiveModalType()}
            onSubmit={(name) => setState({ name })}
          />
        )}
        {activeModalType === 'review' && (
          <ReviewConflictsModal
            conflicts={conflicts}
            onClose={handleSetActiveModalType()}
            onResolve={handleResolveConflicts}
          />
        )}
        {activeModalType === 'timezone' && (
          <TimezoneModal
            timezone={timezone}
            onClose={handleSetActiveModalType()}
            onSubmit={(timezone) => setState({ timezone })}
          />
        )}
      </>
    );
  }

  return (
    <FlexBox flex="1" justifyContent="center" alignItems="center">
      {isError ? (
        <EmptyState
          buttonProps={{
            children: 'Try again',
            onClick: refetchSchedules,
          }}
          description="Something went wrong while fetching the data. Please try again."
          icon="exclamation"
          iconColor="red.500"
          title="Failed to load data"
        />
      ) : (
        <Spinner />
      )}
    </FlexBox>
  );
};
